import { FC, useMemo } from 'react';
import { SubUserRow } from './types';
import { Table } from '@silverfort/sf-components';
import { SUB_USERS_TABLE_COLUMNS } from './consts';
import { User } from '../../types/User';
import { useGetUserDetailsByGuid } from '../../api/queries/useGetUserDetailsByGuid';

interface Props {
    primaryUser: User;
}

const ROW_HEIGHT = 48;

const keyExtractor = (user: SubUserRow) => user.id;

const convertUserToSubUserRow = (user: User, isPrimary: boolean, primaryUserGuid: User['guid']): SubUserRow => {
   
    let email;

    if (user.isManualEmail) {
        email = user.email;
    } else {
        email = user.adEmail ?? '';
    }

    return {
        primaryUserGuid,
        id: user.guid,
        name: {
            isPrimary,
            userName: user.userName
        },
        email: {
            address: email,
            isExternal: user.isExternalEmail,
            isManualEmail: user.isManualEmail
        },
        groups: user.groups,
        lastSeen: user.lastAccess,
        manage: undefined as never
    };
};

const SubUsersTable: FC<Props> = ({ primaryUser }) => {
    const {
        groups = [],
        secondaryUsers = [],
        loading
    } = useGetUserDetailsByGuid({ userGuid: primaryUser.guid }); // todo: handle errors after design from Sarai - https://silverfort.atlassian.net/browse/AF-38308

    const rows = useMemo(() => {
        if (!secondaryUsers.length) {
            return [];
        }

        const primarySubUserRow: SubUserRow = {
            ...convertUserToSubUserRow(primaryUser, true, primaryUser.guid),
            groups
        };

        const secondarySubUserRows =
            secondaryUsers?.map((secondaryUser) => convertUserToSubUserRow(secondaryUser, false, primaryUser.guid)) ??
            [];

        return [primarySubUserRow, ...secondarySubUserRows];
    }, [groups, primaryUser, secondaryUsers]);

    const tableBodyHeight = rows ? rows.length * ROW_HEIGHT : undefined;

    return (
        <Table
            tableContainerClassName="w-full h-fit"
            bodyHeight={tableBodyHeight}
            keyExtractor={keyExtractor}
            columns={SUB_USERS_TABLE_COLUMNS}
            rows={rows}
            loading={loading}
        />
    );
};

export default SubUsersTable;
